import React from 'react';
import { BarChart3, Ship, Construction, Clock, Upload, Activity, PlusCircle} from 'lucide-react';
import CustomDropdown from './CustomDropdown';
import BarLoader from './BarLoader';

const ComparisonSetup = ({
  selectedClass,
  setSelectedClass,
  firstCompareProject,
  setFirstCompareProject,
  firstCompareShip,
  setFirstCompareShip,
  secondCompareProject,
  setSecondCompareProject,
  secondCompareShip,
  setSecondCompareShip,
  firstProjectStatus,
  setFirstProjectStatus,
  secondProjectStatus,
  setSecondProjectStatus,
  handleCompareShips,
  // Removed handleOpenUploadModal, handleOpenAddProjectModal as they are now handled in parent
  isLoadingCompare,
  compareLoaderMessage,
  compareProgress,
  comparisonResults,
  selectedTask1,
  setSelectedTask1,
  selectedTask2,
  setSelectedTask2,
  classes,
  projects,
  firstCompareProjectsOptions,
  secondCompareProjectsOptions,
  firstCompareShipsOptions,
  secondCompareShipsOptions,
  projectStatusOptions,
}) => {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100 mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
        <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
        Ship Comparison Setup
      </h2>

      {/* All Dropdowns in a single responsive row with "VS" integrated */}
      {/* Total items: Class, Project 1, Ship 1, Status 1, VS, Project 2, Ship 2, Status 2 = 8 items */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-x-4 gap-y-6 items-end">
        {/* Class Selection */}
        <div className="col-span-1">
          <label htmlFor="class-select" className="block text-sm font-medium text-gray-700 mb-1">Class</label>
          <CustomDropdown
            id="class-select"
            value={selectedClass}
            onChange={setSelectedClass} // Now setSelectedClass in parent will handle clearing other states and fetching filtered projects
            options={classes}
            placeholder="Select Class"
            icon={Ship}
            hasSearch={true}
          />
        </div>

        {/* First Ship Comparison Group */}
        <div className="col-span-1">
          <label htmlFor="status1-select" className="block text-sm font-medium text-gray-700 mb-1">Status 1</label>
          <CustomDropdown
            id="status1-select"
            value={firstProjectStatus}
            onChange={setFirstProjectStatus} // This will now trigger fetching of projects and ships
            options={projectStatusOptions}
            placeholder="Status 1"
            icon={Clock}
            disabled={!selectedClass}
          />
        </div>
        <div className="col-span-1">
          <label htmlFor="project1-select" className="block text-sm font-medium text-gray-700 mb-1">Project 1</label>
          <CustomDropdown
            id="project1-select"
            value={firstCompareProject}
            onChange={setFirstCompareProject} // This will now update ships based on already filtered projects
            options={firstCompareProjectsOptions} // Use the new filtered projects
            placeholder="Project 1"
            icon={Construction}
            disabled={!selectedClass || !firstProjectStatus || firstCompareProjectsOptions.length === 0}
          />
        </div>
        <div className="col-span-1">
          <label htmlFor="ship1-select" className="block text-sm font-medium text-gray-700 mb-1">Ship 1</label>
          <CustomDropdown
            id="ship1-select"
            value={firstCompareShip}
            onChange={setFirstCompareShip}
            options={firstCompareShipsOptions}
            placeholder="Ship 1"
            icon={Ship}
            disabled={!firstCompareProject || firstCompareShipsOptions.length === 0}
          />
        </div>

        {/* VS Separator - Directly in the grid */}
        <div className="col-span-1 flex justify-center items-center text-4xl font-bold text-gray-700">
            <span className="text-blue-500">VS</span>
        </div>

        {/* Second Ship Comparison Group */}
        <div className="col-span-1">
          <label htmlFor="status2-select" className="block text-sm font-medium text-gray-700 mb-1">Status 2</label>
          <CustomDropdown
            id="status2-select"
            value={secondProjectStatus}
            onChange={setSecondProjectStatus} // This will now trigger fetching of projects and ships
            options={projectStatusOptions}
            placeholder="Status 2"
            icon={Clock}
            disabled={!selectedClass}
          />
        </div>
        <div className="col-span-1">
          <label htmlFor="project2-select" className="block text-sm font-medium text-gray-700 mb-1">Project 2</label>
          <CustomDropdown
            id="project2-select"
            value={secondCompareProject}
            onChange={setSecondCompareProject} // This will now update ships based on already filtered projects
            options={secondCompareProjectsOptions} // Use the new filtered projects
            placeholder="Project 2"
            icon={Construction}
            disabled={!selectedClass || !secondProjectStatus || secondCompareProjectsOptions.length === 0}
          />
        </div>
        <div className="col-span-1">
          <label htmlFor="ship2-select" className="block text-sm font-medium text-gray-700 mb-1">Ship 2</label>
          <CustomDropdown
            id="ship2-select"
            value={secondCompareShip}
            onChange={setSecondCompareShip}
            options={secondCompareShipsOptions}
            placeholder="Ship 2"
            icon={Ship}
            disabled={!secondCompareProject || secondCompareShipsOptions.length === 0}
          />
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          className="w-full md:w-1/2 py-3 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
          onClick={handleCompareShips}
          disabled={isLoadingCompare || !firstCompareShip || !secondCompareShip || !firstProjectStatus || !secondProjectStatus}
        >
          {isLoadingCompare ? 'Comparing...' : 'Compare Ships'}
        </button>
      </div>

      {isLoadingCompare && (
          <div className="mt-8 flex items-center justify-center">
              <BarLoader message={compareLoaderMessage} type="upload" currentProgress={compareProgress} />
          </div>
      )}

      {comparisonResults && (
        <div className="mt-8 p-6 bg-white rounded-xl border border-blue-200">
          <h3 className="text-gray-800 font-medium mb-4 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-blue-500" />
            Compare Specific Tasks
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="task1-select" className="block text-sm font-medium text-gray-700 mb-1">Task for Ship 1</label>
              <CustomDropdown
                id="task1-select"
                value={selectedTask1 ? selectedTask1.id : ''}
                onChange={(taskId) => {
                  const task = comparisonResults.task_transactions1.find(t => t.id.toString() === taskId);
                  setSelectedTask1(task);
                }}
                options={comparisonResults.task_transactions1 ? comparisonResults.task_transactions1.map(task => ({ value: task.id.toString(), label: task.name })) : []}
                placeholder="Select Task for Ship 1"
                icon={Clock}
                disabled={!comparisonResults.task_transactions1 || comparisonResults.task_transactions1.length === 0}
              />
              {selectedTask1 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-gray-700">
                  <p className="font-semibold text-gray-800 mb-2">{selectedTask1.name}</p>
                  <p><strong>Planned:</strong> {selectedTask1.start_date} to {selectedTask1.end_date}</p>
                  <p><strong>Actual:</strong> {selectedTask1.actual_start} to {selectedTask1.actual_finish}</p>
                  <p><strong>Completion:</strong> {selectedTask1.actual_completion_percent}%</p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="task2-select" className="block text-sm font-medium text-gray-700 mb-1">Task for Ship 2</label>
              <CustomDropdown
                id="task2-select"
                value={selectedTask2 ? selectedTask2.id : ''}
                onChange={(taskId) => {
                  const task = comparisonResults.task_transactions2.find(t => t.id.toString() === taskId);
                  setSelectedTask2(task);
                }}
                options={comparisonResults.task_transactions2 ? comparisonResults.task_transactions2.map(task => ({ value: task.id.toString(), label: task.name })) : []}
                placeholder="Select Task for Ship 2"
                icon={Clock}
                disabled={!comparisonResults.task_transactions2 || comparisonResults.task_transactions2.length === 0}
              />
              {selectedTask2 && (
                <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-100 text-sm text-gray-700">
                  <p className="font-semibold text-gray-800 mb-2">{selectedTask2.name}</p>
                  <p><strong>Planned:</strong> {selectedTask2.start_date} to {selectedTask2.end_date}</p>
                  <p><strong>Actual:</strong> {selectedTask2.actual_start} to {selectedTask2.actual_finish}</p>
                  <p><strong>Completion:</strong> {selectedTask2.actual_completion_percent}%</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSetup;